import { useEffect, useState } from 'react';
import { useFormatter, useTranslations } from 'use-intl';
import Tooltip from '@/presentation/components/ui/Tooltip';
import Icon from '@/presentation/components/elements/Icon/Icon';
import { Icons } from '@/presentation/components/elements/Icon';

const SLIPPAGE_OPTIONS = ['0.001', '0.005', '0.01'];

interface SettingsProps {
  slippage: string;
  onSlippageChange: (n: string) => void;
}

const Settings = ({ slippage, onSlippageChange }: SettingsProps) => {
  const t = useTranslations('Swap.Settings');
  const { number } = useFormatter();
  const [showNotice, setShowNotice] = useState<'high' | 'low' | null>(null);
  const [custom, setCustom] = useState<string>(() => {
    // if slippage is not one of predefined options then prefill the custom value with it
    if (!SLIPPAGE_OPTIONS.includes(slippage)) {
      // multiply by 100 because it's the <input> value so required for display
      return `${number(Number(slippage) * 100)}`;
    }

    return '';
  });

  const handleCustomChange = (val: string) => {
    setCustom(val);
    if (val) {
      onSlippageChange(`${Number(val) / 100}`);
    }
  };

  // show/hide notices on slippage
  useEffect(() => {
    const slippageNumber = parseFloat(slippage);

    if (slippageNumber >= 0.02) {
      setShowNotice('high');
    } else if (slippageNumber < 0.001) {
      setShowNotice('low');
    } else {
      setShowNotice(null);
    }
  }, [slippage]);

  return (
    <div>
      <h4 className="font-medium mb-2">{t('title')}</h4>
      <section>
        <header className="text-sm font-medium mb-2 inline-flex items-center">
          <span>{t('slippage.label')}</span>
          <Tooltip
            content={
              <div>
                <h4 className="font-bold text-xs mb-1">
                  {t('slippage.tooltip.title')}
                </h4>
                <p className="text-xs max-w-[30ch] text-gray-500">
                  {t('slippage.tooltip.text')}
                </p>
              </div>
            }
            label={
              <span className="ml-1 cursor-help">
                <Icon name={Icons.INFORMATION_CIRCLE} size={20} />
              </span>
            }
          />
        </header>
        <div className={`p-1 rounded-lg border flex space-x-1`}>
          {SLIPPAGE_OPTIONS.map((amount) => {
            return (
              <button
                type="button"
                onClick={() => {
                  onSlippageChange(amount);
                  setCustom('');
                }}
                key={amount}
                className={`text-sm tracking-wider px-3 py-1 border-2 rounded-md bg-gray-100 transition-colors ${
                  slippage === amount
                    ? 'border-pink-500 bg-transparent'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {number(Number(amount), {
                  style: 'percent',
                  minimumSignificantDigits: 1,
                })}
              </button>
            );
          })}
          <label
            className={`pr-3 pl-1 py-1 border-2 rounded-md bg-gray-100 flex items-center cursor-pointer transition-colors ${
              Number(slippage) === Number(custom) / 100
                ? 'border-pink-500'
                : 'border-gray-200 focus-within:border-gray-300 hover:border-gray-300'
            }`}
          >
            <input
              type="number"
              className="tracking-wider text-sm border-none bg-transparent max-w-[6ch] p-0 outline-none focus:ring-0 text-right placeholder:tracking-normal placeholder:text-gray-500"
              value={custom}
              onChange={(e) => handleCustomChange(e.currentTarget.value)}
              onBlur={(e) => {
                // if you leave the field blank set back to default
                if (!e.currentTarget.value) {
                  onSlippageChange(SLIPPAGE_OPTIONS[1]);
                }
              }}
              placeholder={t('slippage.custom')}
            />
            <span className="ml-1 text-sm text-gray-500">{'%'}</span>
          </label>
        </div>
        {showNotice && (
          <aside className="mt-1 text-xs text-red-500 flex items-center">
            <Icon
              name={Icons.EXCLAMATION_TRIANGLE}
              size={20}
              className="mr-1"
            />
            <span>{t(`slippage.alert.${showNotice}`)}</span>
          </aside>
        )}
      </section>
    </div>
  );
};

export default Settings;
